import merge from 'lodash.merge';

import de from './locales/de.ts';
import en from './locales/en.ts';
import pl from './locales/pl.ts';
import sk from './locales/sk.ts';

const SUPPORTED_LOCALES = [
  'en',
  'de',
  'pl',
  'sk',
] as const;
type TSupportedLocale = typeof SUPPORTED_LOCALES[number];

interface ITranslation {
  [key: string]: ITranslation | string,
}

type TImportedTranslation = {
  default: ITranslation,
};

function buildKeyForPath(path: string): string {
  return path
    .replace(/^\.\.\//, '') // remove leading ../
    .replace(/\.i18n\.[a-z-]+\.ts$/, ''); // remove .i18n.xx.ts
}

function collectMessages(locale: TSupportedLocale): ITranslation {
  let translationImports: Record<string, TImportedTranslation>;
  let exhaustiveCheck: never;
  switch (locale) {
    case 'en':
      translationImports = {
        ...import.meta.glob('../components/**/*.i18n.en.ts', {
          eager: true,
        }),
        ...import.meta.glob('../composable/**/*.i18n.en.ts', {
          eager: true,
        }),
      };
      break;
    case 'de':
      translationImports = {
        ...import.meta.glob('../components/**/*.i18n.de.ts', {
          eager: true,
        }),
        ...import.meta.glob('../composable/**/*.i18n.de.ts', {
          eager: true,
        }),
      };
      break;
    case 'pl':
      translationImports = {
        ...import.meta.glob('../components/**/*.i18n.pl.ts', {
          eager: true,
        }),
        ...import.meta.glob('../composable/**/*.i18n.pl.ts', {
          eager: true,
        }),
      };
      break;
    case 'sk':
      translationImports = {
        ...import.meta.glob('../components/**/*.i18n.sk.ts', {
          eager: true,
        }),
        ...import.meta.glob('../composable/**/*.i18n.sk.ts', {
          eager: true,
        }),
      };
      break;
    default:
      exhaustiveCheck = locale;
      throw new Error(`Failed to provide translation import for locale ${exhaustiveCheck}`);
  }

  return Object.entries(translationImports).reduce((messages, [
    filePath,
    module,
  ]) => {
    const translationKey = buildKeyForPath(filePath);
    return merge(messages, {
      [translationKey]: module.default,
    });
  }, {} as ITranslation);
}

export const messages = SUPPORTED_LOCALES.reduce(
  (messages, locale) => {
    messages[locale] = merge(messages[locale], collectMessages(locale));
    return messages;
  },
  {
    de,
    en,
    pl,
    sk,
  } as Record<TSupportedLocale, ITranslation>,
);
